import { useEffect, useState } from "react";
import { HiOutlinePhoto } from "react-icons/hi2";
import { chatApi } from "../../api/chat";
import { IChatAttachment } from "../../data-access";

/**
 * Object URLs are cached per attachment id so the image is fetched once and
 * survives the re-render every poll tick causes.
 *
 * The cache is bounded: the widget lives outside <Routes> and never unmounts
 * while the tab is open, so without a ceiling every screenshot a user ever
 * opened would stay in memory for the lifetime of the tab. Entries are evicted
 * oldest first, but only once nothing on screen is still showing them - a
 * mounted image holds its entry, so eviction can never break a visible
 * screenshot.
 */
const MAX_CACHED_ATTACHMENTS = 30;

// Insertion-ordered, which makes "oldest first" a plain iteration.
const blobUrlCache = new Map<string, string>();
const inFlight = new Map<
  string,
  { promise: Promise<string>; controller: AbortController }
>();
const holders = new Map<string, number>();

const evictUnused = (): void => {
  if (blobUrlCache.size <= MAX_CACHED_ATTACHMENTS) {
    return;
  }

  Array.from(blobUrlCache.keys()).forEach((attachmentId) => {
    if (blobUrlCache.size <= MAX_CACHED_ATTACHMENTS) {
      return;
    }

    if (holders.get(attachmentId)) {
      return;
    }

    URL.revokeObjectURL(blobUrlCache.get(attachmentId) as string);
    blobUrlCache.delete(attachmentId);
  });
};

const loadAttachmentUrl = (attachmentId: string): Promise<string> => {
  const cachedUrl = blobUrlCache.get(attachmentId);

  if (cachedUrl) {
    // Re-inserting moves the entry to the young end of the eviction order.
    blobUrlCache.delete(attachmentId);
    blobUrlCache.set(attachmentId, cachedUrl);

    return Promise.resolve(cachedUrl);
  }

  const pending = inFlight.get(attachmentId);

  if (pending) {
    return pending.promise;
  }

  const controller = new AbortController();
  const promise = chatApi
    .getAttachmentBlob(attachmentId, controller.signal)
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob);

      blobUrlCache.set(attachmentId, objectUrl);
      inFlight.delete(attachmentId);
      evictUnused();

      return objectUrl;
    })
    .catch((error) => {
      inFlight.delete(attachmentId);

      return Promise.reject(error);
    });

  inFlight.set(attachmentId, { promise, controller });

  return promise;
};

const acquireAttachment = (attachmentId: string): void => {
  holders.set(attachmentId, (holders.get(attachmentId) ?? 0) + 1);
};

const releaseAttachment = (attachmentId: string): void => {
  const next = (holders.get(attachmentId) ?? 0) - 1;

  if (next > 0) {
    holders.set(attachmentId, next);
  } else {
    holders.delete(attachmentId);
  }

  evictUnused();
};

/**
 * Drops everything: revokes the cached object URLs and aborts the downloads
 * still running, so closing the widget or switching account leaves no bytes and
 * no request behind.
 */
export const releaseChatImageCache = (): void => {
  inFlight.forEach((entry) => entry.controller.abort());
  inFlight.clear();

  blobUrlCache.forEach((objectUrl) => URL.revokeObjectURL(objectUrl));
  blobUrlCache.clear();
  holders.clear();
};

interface IChatImageProps {
  attachment: IChatAttachment;
  onLoaded?: () => void;
}

export const ChatImage = ({
  attachment,
  onLoaded,
}: IChatImageProps): JSX.Element => {
  const [objectUrl, setObjectUrl] = useState<string | null>(
    blobUrlCache.get(attachment.id) ?? null
  );
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    let isActive = true;

    acquireAttachment(attachment.id);

    loadAttachmentUrl(attachment.id)
      .then((url) => {
        if (isActive) {
          setObjectUrl(url);
        }
      })
      .catch(() => {
        if (isActive) {
          setHasFailed(true);
        }
      });

    return () => {
      isActive = false;
      releaseAttachment(attachment.id);
    };
  }, [attachment.id]);

  if (hasFailed) {
    return (
      <div className="support-chat__attachment support-chat__attachment--failed">
        <HiOutlinePhoto size={18} />
        <span>{attachment.name}</span>
      </div>
    );
  }

  if (!objectUrl) {
    return <div className="support-chat__attachment-skeleton" />;
  }

  return (
    <a
      className="support-chat__attachment"
      href={objectUrl}
      target="_blank"
      rel="noreferrer"
    >
      <img src={objectUrl} alt={attachment.name} onLoad={onLoaded} />
    </a>
  );
};
